import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARRIERS = ["MSC", "Maersk", "CMA CGM", "Hapag-Lloyd", "ONE", "Evergreen", "ZIM", "No preference"];
const CONTAINERS = ["20GP", "40GP", "40HQ", "Reefer", "LCL"];
const ORIGINS = ["Mersin Port", "Istanbul (Ambarli)", "Izmir (Aliaga)", "Gemlik", "Other Turkish Port"];

export default function BookingModal({ open, onOpenChange, intent }) {
  const isWorkspace = intent === "workspace";

  const [form, setForm] = useState({
    company: "",
    fullName: "",
    email: "",
    phone: "",
    origin: "Mersin Port",
    destination: "",
    container: "40HQ",
    carrier: "No preference",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setDone(false);
    setForm({
      company: "",
      fullName: "",
      email: "",
      phone: "",
      origin: "Mersin Port",
      destination: "",
      container: "40HQ",
      carrier: "No preference",
      notes: "",
    });
  };

  const handleClose = (v) => {
    if (!v) {
      onOpenChange(false);
      setTimeout(reset, 250);
    } else {
      onOpenChange(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        intent: isWorkspace ? "workspace" : "booking",
      });
      setDone(true);
      toast.success(
        isWorkspace
          ? "Workspace request received."
          : "Booking request received.",
      );
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="booking-modal"
        className="sm:max-w-[640px] bg-white border-slate-200 p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        {done ? (
          <div className="p-10 text-center">
            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h3 className="mt-5 font-display font-extrabold text-2xl tracking-tight text-[#0B1F3A]">
              Request received.
            </h3>
            <p className="mt-2 text-slate-600">
              Our team will reach out within one business day with your
              {isWorkspace ? " Workspace invitation" : " booking confirmation"}.
            </p>
            <Button
              data-testid="booking-modal-close"
              onClick={() => handleClose(false)}
              className="mt-6 rounded-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white px-6"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className="px-7 pt-7 pb-5 border-b border-slate-100 bg-slate-50/60">
              <DialogHeader>
                <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#3B82F6]">
                  {isWorkspace ? "Workspace" : "FreightIQ™"}
                </div>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[#0B1F3A]">
                  {isWorkspace
                    ? "Create your free Workspace"
                    : "Book your shipment from Turkey"}
                </DialogTitle>
                <DialogDescription className="text-slate-500">
                  {isWorkspace
                    ? "Spin up a connected Workspace with FreightIQ™, Inspection™ and Live Tracking™."
                    : "Tell us about your shipment. We'll coordinate carrier confirmation in the Workspace."}
                </DialogDescription>
              </DialogHeader>
            </div>

            <form onSubmit={submit} className="px-7 py-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <Input
                    data-testid="booking-input-name"
                    value={form.fullName}
                    onChange={(e) => update("fullName", e.target.value)}
                    placeholder="Jane Cooper"
                    required
                  />
                </Field>
                <Field label="Company">
                  <Input
                    data-testid="booking-input-company"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Cooper Imports Ltd."
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Work email" required>
                  <Input
                    data-testid="booking-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@company.com"
                    required
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    data-testid="booking-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+90 ..."
                  />
                </Field>
              </div>

              {!isWorkspace && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Origin (Turkey)">
                      <Select
                        value={form.origin}
                        onValueChange={(v) => update("origin", v)}
                      >
                        <SelectTrigger data-testid="booking-select-origin">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORIGINS.map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Destination port">
                      <Input
                        data-testid="booking-input-destination"
                        value={form.destination}
                        onChange={(e) => update("destination", e.target.value)}
                        placeholder="Mombasa, KEMBA"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Container">
                      <Select
                        value={form.container}
                        onValueChange={(v) => update("container", v)}
                      >
                        <SelectTrigger data-testid="booking-select-container">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTAINERS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Carrier preference">
                      <Select
                        value={form.carrier}
                        onValueChange={(v) => update("carrier", v)}
                      >
                        <SelectTrigger data-testid="booking-select-carrier">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CARRIERS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                </>
              )}

              <Field
                label={isWorkspace ? "Tell us about your imports" : "Notes"}
              >
                <Textarea
                  data-testid="booking-input-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder={
                    isWorkspace
                      ? "Volumes, sourcing regions, teams..."
                      : "Cargo description, ready date, special handling..."
                  }
                  rows={3}
                />
              </Field>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleClose(false)}
                  className="text-slate-500 hover:text-[#0B1F3A]"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="booking-submit"
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white px-6 h-11 font-semibold"
                >
                  {submitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isWorkspace ? "Create Workspace" : "Submit Booking Request"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs tracking-[0.12em] uppercase font-bold text-slate-500">
      {label}
      {required && <span className="text-[#3B82F6] ml-0.5">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
